import {
    useCallback,
    useState
} from "react";
import {
    useWindowDimensions,
    ActivityIndicator,
    TouchableOpacity,
    Linking,
    Image,
    Modal,
    View
} from "react-native";
import type {
    FormSubmissionType,
    FormDetailPayload
} from "./type";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    NCoreUIKitToast,
    PageContainer,
    TextInput,
    Seperator,
    RowCard,
    Sticker,
    Switch,
    Button,
    Text
} from "ncore-ui-kit";
import {
    useFocusEffect,
    useNavigation,
    useRoute
} from "@react-navigation/native";
import type RootStackParamList from "../../../../navigation/type";
import type {
    NativeStackNavigationProp,
    NativeStackScreenProps
} from "@react-navigation/native-stack";
import {
    ChevronDownIcon,
    ArrowLeftIcon,
    ChevronUpIcon,
    SearchIcon,
    CopyIcon,
    XIcon
} from "lucide-react-native";
import RESTServiceContext from "../../../../services/restAPI/context";

type Props = NativeStackScreenProps<RootStackParamList, "FormDetail">;

const FormDetail = () => {
    const {
        width
    } = useWindowDimensions();
    const isMobile = width < 768;

    const route = useRoute<Props["route"]>();
    const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();

    const formID = route.params?.formID;

    const [
        isDuplicateModalVisible,
        setIsDuplicateModalVisible
    ] = useState(false);

    const [
        duplicateTitle,
        setDuplicateTitle
    ] = useState("");

    const [
        duplicateDescription,
        setDuplicateDescription
    ] = useState("");

    const [
        isCopyAnswers,
        setIsCopyAnswers
    ] = useState(false);
    const [
        expandedRowIDs,
        setExpandedRowIDs
    ] = useState<string[]>([]);

    const [
        formData,
        setFormData
    ] = useState<FormDetailPayload | null>(null);

    const [
        loading,
        setLoading
    ] = useState<boolean>(true);

    const [
        searchQuery,
        setSearchQuery
    ] = useState<string>("");

    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const {
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    const fetchFormDetail = useCallback(async () => {
        if(!formID) {
            return;
        }

        try {
            setLoading(true);
            const result = await RESTServiceContext.action("getFormAnswersForAdmin", {
                formID: formID
            });

            if(result.payload) {
                setFormData(result.payload);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    }, [
        formID
    ]);

    useFocusEffect(
        useCallback(() => {
            fetchFormDetail();
        }, [
            fetchFormDetail
        ])
    );

    const toggleRow = (
        submissionID: string
    ) => {
        setExpandedRowIDs(prev =>
            prev.includes(submissionID) ? prev.filter(id => id !== submissionID) : [
                ...prev,
                submissionID
            ]
        );
    };

    const openDuplicateModal = () => {
        setDuplicateTitle("");
        setDuplicateDescription("");
        setIsCopyAnswers(false);
        setIsDuplicateModalVisible(true);
    };

    const handleDuplicateForm = async () => {
        if(!formID) return;
        try {
            const result = await RESTServiceContext.action("duplicateFormForAdmin", {
                formID,
                ...(duplicateTitle.trim() && {
                    title: duplicateTitle.trim()
                }),
                ...(duplicateDescription.trim() && {
                    description: duplicateDescription.trim()
                }),
                isCopyAnswers
            });
            if (result.code === 201) {
                NCoreUIKitToast.open({
                    title: localize("forms-detail-duplicate-success"),
                    type: "success"
                });
                setIsDuplicateModalVisible(false);
                navigation.goBack();
            }
        } catch (error) {
            console.error(error);
            NCoreUIKitToast.open({
                title: localize("forms-error-general"),
                type: "danger"
            });
        }
    };

    const renderDuplicateModal = () => {
        return <Modal
            onRequestClose={() => setIsDuplicateModalVisible(false)}
            visible={isDuplicateModalVisible}
            animationType="fade"
            transparent={true}
        >
            <View
                style={[
                    stylesheet.modalOverlay,
                    {
                        backgroundColor: colors.content.container.overlay
                    }
                ]}
            >
                <View
                    style={{
                        backgroundColor: colors.project.brandWhite,
                        width: isMobile ? "90%" : 400,
                        padding: spaces.spacingXl,
                        borderRadius: radiuses.lg,
                        gap: spaces.spacingLg
                    }}
                >
                    <View
                        style={stylesheet.modalHeader}
                    >
                        <Text
                            variant="titleLargeSize"
                        >
                            {localize("forms-detail-duplicate-modal-title")}
                        </Text>
                        <TouchableOpacity
                            onPress={() => setIsDuplicateModalVisible(false)}
                        >
                            <XIcon
                                color={colors.content.icon.mid}
                                size={24}
                            />
                        </TouchableOpacity>
                    </View>
                    <TextInput
                        placeholder={formData?.title || localize("forms-detail-duplicate-modal-title-placeholder")}
                        title={localize("forms-detail-duplicate-modal-title-input")}
                        customColor={colors.project.inputBackground}
                        onChangeText={setDuplicateTitle}
                        spreadBehaviour="stretch"
                        value={duplicateTitle}
                    />
                    <TextInput
                        placeholder={formData?.description || localize("forms-detail-duplicate-modal-desc-placeholder")}
                        title={localize("forms-detail-duplicate-modal-desc-input")}
                        customColor={colors.project.inputBackground}
                        onChangeText={setDuplicateDescription}
                        value={duplicateDescription}
                        spreadBehaviour="stretch"
                    />
                    <Switch
                        titleStyle={{
                            color: isCopyAnswers ? colors.project.brandWhite : colors.content.text.high
                        }}
                        title={localize("forms-detail-duplicate-modal-copy-answers")}
                        onPress={() => setIsCopyAnswers(!isCopyAnswers)}
                        customTheme={{
                            themeKey: "events_form" as never
                        }}
                        isActive={isCopyAnswers}
                        type="neutral"
                    />
                    <View
                        style={{
                            justifyContent: "flex-end",
                            flexDirection: "row",
                            gap: spaces.spacingMd
                        }}
                    >
                        <Button
                            onPress={() => setIsDuplicateModalVisible(false)}
                            title={localize("cancel") || "İptal"}
                            variant="filled"
                            type="neutral"
                        />
                        <Button
                            title={localize("forms-detail-duplicate-modal-title")}
                            onPress={handleDuplicateForm}
                            variant="filled"
                        />
                    </View>
                </View>
            </View>
        </Modal>;
    };

    const renderHeader = () => {
        return <View
            style={[
                stylesheet.headerContainer,
                {
                    marginBottom: spaces.spacingXl
                }
            ]}
        >
            <View
                style={{
                    flexDirection: "row",
                    alignItems: "center",
                    gap: spaces.spacingSm
                }}
            >
                <Button
                    onPress={() => navigation.navigate("Forms")}
                    icon={() => <ArrowLeftIcon
                        color={colors.project.brandWhite}
                        size={24}
                    />}
                    customBorderColor="secondaryButton"
                    customColor="secondaryButton"
                    spreadBehaviour="free"
                    variant="filled"
                />
                <Text
                    variant="titleLargeSize"
                >
                    {localize("forms-detail-title")}
                </Text>
            </View>
            <Button
                title={localize("forms-detail-duplicate")}
                icon={({
                    color,
                    size
                }) => <CopyIcon
                    color={colors.content.icon[color]}
                    size={size}
                />}
                customBorderColor="secondaryButton"
                customColor="secondaryButton"
                onPress={openDuplicateModal}
                spreadBehaviour="free"
                variant="filled"
            />
        </View>;
    };

    const renderSubmissionRow = (
        submission: FormSubmissionType,
        index: number
    ) => {
        const isExpanded = expandedRowIDs.includes(submission.applicationID || "");
        const nameSurname = `${submission.userDetails.name} ${submission.userDetails.surname}`;
        const phone = submission.userDetails.phone || submission.userDetails.email || "-";

        const dateObj = new Date(submission.createdAt);
        const dateStr = !isNaN(dateObj.getTime()) ? dateObj.toLocaleDateString() : "-";

        return <View
            style={[
                stylesheet.submissionCardContainer,
                {
                    backgroundColor: colors.project.brandWhite,
                    borderColor: colors.project.brandBorder,
                    marginBottom: spaces.spacingMd,
                    borderWidth: borders.seperator,
                    borderRadius: radiuses.md
                }
            ]}
            key={submission.applicationID || `sub-${index}`}
        >
            <RowCard
                onPress={() => toggleRow(submission.applicationID || "")}
                rightIcon={({
                    color,
                    size
                }) => isExpanded ? <ChevronUpIcon
                    color={colors.content.icon[color]}
                    size={size}
                /> : <ChevronDownIcon
                    color={colors.content.icon[color]}
                    size={size}
                />}
                rightSubTitleVariant="bodySmallSize"
                rightTitleVariant="bodyMediumSize"
                subTitleVariant="bodySmallSize"
                titleVariant="titleMediumSize"
                rightSubTitleColor="low"
                rightSubTitle={dateStr}
                rightTitleColor="mid"
                subTitleColor="low"
                title={nameSurname}
                rightTitle={phone}
                subTitle={dateStr}
                titleColor="high"
                iconSize={20}
            />
            {
                isExpanded ?
                    <View
                        style={[
                            stylesheet.expandedContent,
                            {
                                borderTopColor: colors.project.brandBorder,
                                borderTopWidth: borders.seperator,
                                padding: spaces.spacingMd,
                                gap: spaces.spacingMd
                            }
                        ]}
                    >
                        {
                            submission.answers && submission.answers.length > 0 ?
                                submission.answers.map((ans, ansIndex) => {
                                    return <View
                                        key={ans.key || `ans-${ansIndex}`}
                                        style={[
                                            stylesheet.answerRow,
                                            {
                                                gap: spaces.spacingXs
                                            }
                                        ]}
                                    >
                                        <Text
                                            style={{
                                                color: colors.content.text.mid
                                            }}
                                            variant="bodySmallSize"
                                        >
                                            {ans.title}
                                        </Text>
                                        {
                                            ans.type === "photo" && typeof ans.value === "string" && ans.value.length > 0 ?
                                                <TouchableOpacity
                                                    onPress={() => Linking.openURL(ans.value as string)}
                                                >
                                                    <Image
                                                        style={[
                                                            stylesheet.previewImage,
                                                            {
                                                                marginTop: spaces.spacingXs,
                                                                borderRadius: radiuses.md
                                                            }
                                                        ]}
                                                        source={{
                                                            uri: ans.value as string
                                                        }}
                                                        resizeMode="cover"
                                                    />
                                                </TouchableOpacity>
                                            :
                                                ans.type === "yes_no" ?
                                                    <View
                                                        style={{
                                                            marginTop: spaces.spacingXs,
                                                            alignItems: "flex-start"
                                                        }}
                                                    >
                                                        <Sticker
                                                            title={ans.value === "yes" || ans.value === "true" ? (localize("forms-create-confirm-yes") || "Evet") : (localize("forms-create-confirm-no") || "Hayır")}
                                                            type={ans.value === "yes" || ans.value === "true" ? "success" : "danger"}
                                                            spreadBehaviour="free"
                                                            size="small"
                                                        />
                                                    </View>
                                                :
                                                    <Text
                                                        variant="bodyMediumSize"
                                                    >
                                                        {Array.isArray(ans.value) ? ans.value.join(", ") : (ans.value || "-")}
                                                    </Text>
                                        }
                                    </View>;
                                })
                            :
                                <Text
                                    style={{
                                        color: colors.content.text.low
                                    }}
                                    variant="bodyMediumSize"
                                >
                                    {localize("forms-detail-no-answers")}
                                </Text>
                        }
                    </View>
                :
                    null
            }
        </View>;
    };

    const filteredSubmissions = formData?.submissions?.filter((submission) => {
        if(!searchQuery) return true;

        const nameSurname = `${submission.userDetails.name || ""} ${submission.userDetails.surname || ""}`.toLowerCase();
        const email = (submission.userDetails.email || "").toLowerCase();
        const phone = (submission.userDetails.phone || "").toLowerCase();

        const query = searchQuery.toLowerCase();
        return nameSurname.includes(query) || email.includes(query) || phone.includes(query);
    }) || [];

    if(loading) {
        return <PageContainer>
            <View
                style={stylesheet.loadingContainer}
            >
                <ActivityIndicator
                    color={colors.project.gold}
                    size="large"
                />
            </View>
        </PageContainer>;
    }

    if(!formData) {
        return <PageContainer>
            <View
                style={[
                    stylesheet.emptyContainer,
                    {
                        padding: spaces.spacingXl
                    }
                ]}
            >
                <Text>{localize("forms-detail-not-found")}</Text>
            </View>
        </PageContainer>;
    }

    return <PageContainer
        scrollViewStyle={[
            stylesheet.container,
            {
                padding: spaces.spacingXl,
                gap: spaces.spacingXl
            }
        ]}
        isScrollable={true}
    >
        {renderHeader()}
        <View
            style={[
                stylesheet.infoCard,
                {
                    backgroundColor: colors.project.brandWhite,
                    borderColor: colors.project.brandBorder,
                    borderWidth: borders.seperator,
                    padding: spaces.spacingXl,
                    borderRadius: radiuses.lg,
                    gap: spaces.spacingXl
                }
            ]}
        >
            <View
                style={[
                    stylesheet.infoHeader,
                    {
                        gap: spaces.spacingXs
                    }
                ]}
            >
                <Text
                    variant="displaySmallSize"
                >
                    {formData.title || "-"}
                </Text>
                <Text
                    style={{
                        color: colors.content.text.mid
                    }}
                    variant="titleMediumSize"
                >
                    {formData.description || "-"}
                </Text>
            </View>
            <Seperator
                color="subtle"
            />
            <View
                style={[
                    stylesheet.detailSection,
                    {
                        gap: spaces.spacingMd
                    }
                ]}
            >
                <Text
                    variant="titleMediumSize"
                >
                    {localize("forms-detail-general-info")}
                </Text>
                <View
                    style={[
                        stylesheet.detailRow,
                        {
                            flexDirection: isMobile ? "column" : "row",
                            gap: spaces.spacingMd
                        }
                    ]}
                >
                    <View
                        style={stylesheet.detailCol}
                    >
                        <Text
                            style={{
                                color: colors.content.text.mid
                            }}
                            variant="bodySmallSize"
                        >
                            {localize("forms-detail-only-members")}
                        </Text>
                        <Text
                            variant="bodyMediumSize"
                        >
                            {formData.isOnlyMembers ? localize("yes") : localize("no")}
                        </Text>
                    </View>
                    <View
                        style={stylesheet.detailCol}
                    >
                        <Text
                            style={{
                                color: colors.content.text.mid
                            }}
                            variant="bodySmallSize"
                        >
                            {localize("forms-detail-created-at")}
                        </Text>
                        <Text
                            variant="bodyMediumSize"
                        >
                            {formData.createdAt ? new Date(formData.createdAt).toLocaleDateString() : "-"}
                        </Text>
                    </View>
                </View>
            </View>
            <Seperator
                color="subtle"
            />
            <View
                style={[
                    stylesheet.detailSection,
                    {
                        gap: spaces.spacingMd
                    }
                ]}
            >
                <Text
                    variant="titleMediumSize"
                >
                    {localize("forms-detail-participant-answers")}
                </Text>
                <TextInput
                    placeholder={localize("forms-list-search-placeholder")}
                    icon={({
                        color,
                        size
                    }) => <SearchIcon
                        color={colors.content.icon[color]}
                        size={size}
                    />}
                    onChangeText={setSearchQuery}
                    spreadBehaviour="stretch"
                    value={searchQuery}
                />
                {
                    filteredSubmissions && filteredSubmissions.length > 0 ?
                        filteredSubmissions.map(renderSubmissionRow)
                    :
                        <View
                            style={[
                                stylesheet.emptyContainer,
                                {
                                    paddingVertical: spaces.spacingXl
                                }
                            ]}
                        >
                            <Text
                                style={{
                                    color: colors.content.text.mid
                                }}
                                variant="bodyMediumSize"
                            >
                                {localize("forms-detail-no-answers")}
                            </Text>
                        </View>
                }
            </View>
        </View>
        {renderDuplicateModal()}
    </PageContainer>;
};

export default FormDetail;
